/**
 * Pure Plotly chart builders for the MARS Portfolio Risk Metrics section.
 *
 * No React — these turn `RiskMetricRow[]` into ready-to-render
 * `{ data, layout }` pairs. `RiskMetricsUnified.tsx` composes them.
 *
 * Each metric renders as one of three chart shapes:
 *   - single series  -> one "Portfolio" line
 *   - dual series    -> "Portfolio" + faint "Benchmark" lines
 *   - contribution   -> a stacked bar per asset class
 */
import type { Data, Layout } from 'plotly.js';

import { hoverTemplate } from '@/lib/plotly/hoverTemplates';
import { chartLayout } from '@/lib/plotly/layoutPresets';
import { AXIS_GRID, AXIS_LINE } from '@/lib/plotly/defaults';
import { getRecessionShapes } from '@/lib/plotly/recessionShapes';
import { ASSET_COLORS_WITH_ALIASES as ASSET_COLORS, hexToRgba } from '@/lib/plotly/colors';

import { getXAxisTickConfig } from './utils';
import type { Fund, MetricType, RiskMetricRow } from '../types';

export interface MetricMeta {
  id: MetricType;
  title: string;
  singleSeries: boolean;
  isContribution: boolean;
}

export interface BuiltRiskPlot {
  data: Data[];
  layout: Partial<Layout>;
}

/** Inner-tick axis settings shared by every risk-metric x/y axis. */
const INNER_TICKS = {
  ...AXIS_GRID,
  ...AXIS_LINE,
  ticks: 'inside' as const,
  ticklen: 8,
  tickwidth: 1,
  tickcolor: '#000000',
};

/** Scale a fractional value (0.15) to percentage points (15), keeping nulls. */
function toPercent(v: number | null | undefined): number | null {
  if (v === null || v === undefined) return null;
  return v * 100;
}

/** Sort rows ascending by observation date. */
function sortByDate(rows: RiskMetricRow[]): RiskMetricRow[] {
  return [...rows].sort((a, b) => new Date(a.obs_date).getTime() - new Date(b.obs_date).getTime());
}

/** A single "Portfolio" line trace. */
function buildSingleSeriesTrace(data: RiskMetricRow[]): Data {
  const sorted = sortByDate(data);
  return {
    type: 'scatter',
    mode: 'lines',
    name: 'Portfolio',
    x: sorted.map((d) => d.obs_date),
    y: sorted.map((d) => toPercent(d.val)),
    line: { color: ASSET_COLORS.Portfolio, width: 2 },
    hovertemplate: hoverTemplate({ name: 'Portfolio', format: '.1f', suffix: '%' }),
  };
}

/** "Portfolio" + (for relative funds) a faint "Benchmark" line. */
function buildDualSeriesTraces(data: RiskMetricRow[], fund: Fund): Data[] {
  const showBenchmark = fund.risk_metrics_type !== 'absolute';
  const portfolio = data.filter((d) => d.series_name?.includes('portfolio'));
  const benchmark = data.filter((d) => d.series_name?.includes('benchmark'));

  const traces: Data[] = [];

  if (showBenchmark && benchmark.length > 0) {
    const sorted = sortByDate(benchmark);
    // Fade the benchmark line only when a portfolio line shares the chart.
    const color =
      portfolio.length > 0 ? hexToRgba(ASSET_COLORS.Benchmark, 0.5) : ASSET_COLORS.Benchmark;
    traces.push({
      type: 'scatter',
      mode: 'lines',
      name: 'Benchmark',
      x: sorted.map((d) => d.obs_date),
      y: sorted.map((d) => toPercent(d.val)),
      line: { color, width: 2 },
      hovertemplate: hoverTemplate({ name: 'Benchmark', format: '.1f', suffix: '%' }),
    });
  }

  if (portfolio.length > 0) {
    const sorted = sortByDate(portfolio);
    traces.push({
      type: 'scatter',
      mode: 'lines',
      name: 'Portfolio',
      x: sorted.map((d) => d.obs_date),
      y: sorted.map((d) => toPercent(d.val)),
      line: { color: ASSET_COLORS.Portfolio, width: 2 },
      hovertemplate: hoverTemplate({ name: 'Portfolio', format: '.1f', suffix: '%' }),
    });
  }

  // Fall back to a single series when no portfolio/benchmark split exists.
  if (traces.length === 0) traces.push(buildSingleSeriesTrace(data));
  return traces;
}

/** One stacked bar per asset class. */
function buildStackedBarTraces(data: RiskMetricRow[], fund: Fund): Data[] {
  const assets =
    fund.asset_classes.length > 0 ? fund.asset_classes : ['Equities', 'Bonds', 'Commodities'];

  const allDates = Array.from(new Set(data.map((d) => d.obs_date))).sort(
    (a, b) => new Date(a).getTime() - new Date(b).getTime(),
  );

  return assets.flatMap((asset) => {
    const rows = data.filter((d) => d.asset_class === asset);
    if (rows.length === 0) return [];

    const valueByDate = new Map<string, number>();
    rows.forEach((d) => {
      const v = toPercent(d.val);
      if (v !== null) valueByDate.set(d.obs_date, v);
    });

    return [
      {
        type: 'bar',
        name: asset,
        x: allDates,
        y: allDates.map((date) => valueByDate.get(date) ?? 0),
        marker: { color: ASSET_COLORS[asset] ?? '#666' },
        hovertemplate: hoverTemplate({ name: asset, format: '.1f', suffix: '%' }),
      } as Data,
    ];
  });
}

/**
 * X-axis range/tick settings derived from the data's actual time span. The
 * range is extended one month past the last observation.
 */
function deriveXAxis(traces: Data[]): {
  tickConfig: { tickformat: string; dtick: string };
  range?: [string, string];
} {
  const allX = traces.flatMap((t) => ((t as { x?: unknown }).x as string[]) ?? []);
  if (allX.length <= 1) {
    return { tickConfig: { tickformat: '%b-%y', dtick: 'M6' } };
  }

  const first = new Date(allX[0]);
  const last = new Date(allX[allX.length - 1]);
  const years = (last.getTime() - first.getTime()) / (365.25 * 24 * 3600 * 1000);
  const extendedDate = new Date(last.getFullYear(), last.getMonth() + 1, last.getDate());

  return {
    tickConfig: getXAxisTickConfig(years),
    range: [allX[0], extendedDate.toISOString().slice(0, 10)],
  };
}

/** Build one risk-metric chart's `{ data, layout }`. */
export function buildRiskMetricPlot(
  meta: MetricMeta,
  fund: Fund,
  data: RiskMetricRow[] | undefined,
): BuiltRiskPlot {
  if (!data || data.length === 0) return { data: [], layout: {} };

  let traces: Data[];
  if (meta.isContribution) traces = buildStackedBarTraces(data, fund);
  else if (meta.singleSeries) traces = [buildSingleSeriesTrace(data)];
  else traces = buildDualSeriesTraces(data, fund);

  const xAxis = deriveXAxis(traces);

  const preset = chartLayout({
    family: meta.isContribution ? 'bar' : 'timeseries',
    marginOverride: { t: 90, b: 70, l: 56, r: 24 },
  });
  const layout: Partial<Layout> = {
    // Convention: title at top, legend BETWEEN title and chart drawing area.
    ...preset,
    title: { text: meta.title, font: { size: 18 }, y: 0.98, yanchor: 'top' },
    // Legacy `mars-portfolio-risk-metrics-unified.js` sets the legend to 14;
    // the shared preset now defaults to 16, so pin it back.
    legend: { ...(preset.legend as object), font: { size: 14 } },
    barmode: meta.isContribution ? 'stack' : undefined,
    xaxis: {
      type: 'date',
      tickformat: xAxis.tickConfig.tickformat,
      dtick: xAxis.tickConfig.dtick,
      ...INNER_TICKS,
      ...(xAxis.range ? { range: xAxis.range } : {}),
    },
    // Contribution bars are clamped to 0-100%; line metrics autorange.
    yaxis: meta.isContribution
      ? { ticksuffix: '%', range: [0, 100], ...INNER_TICKS }
      : { ticksuffix: '%', autorange: true, ...INNER_TICKS },
    shapes: xAxis.range ? getRecessionShapes({ plotXRange: xAxis.range }) : [],
  };

  return { data: traces, layout };
}
